import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { commonSpacing, theme } from "../../shared/theme/theme";
import {
  useGetApplicationsMutation,
  useGetUserQuery,
} from "src/store/services/privateApi";
import { Wrapper } from "src/components";
import i18n from "src/locale/i18n";
import MenuIcon from "@mui/icons-material/Menu";
import Footer from "../Account/Register/Footer";
import { getApplicationAction } from "src/helpers/helperApplications";
import { securityOptions } from "src/shared/config/storeConfig";

export const ApplicationsListScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, user } = useAuth0();
  // Ops "Login as user" / correction grants have no Auth0 session — PrivateRoute
  // already admits them via hasOpsGrant. This page used to require Auth0 only,
  // so staff opened /settings/applications and got "Sign in" with an empty list.
  const hasOpsGrant = securityOptions.hasOpsGrant();
  const canViewApplications = hasOpsGrant || isAuthenticated;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lng: string) => {
    i18n.changeLanguage(lng);
    handleMenuClose();
  };

  const [applicationsList, setApplicationsList] = useState<any[]>([]);
  const [getApplications] = useGetApplicationsMutation();
  const { data: userData } = useGetUserQuery(null, { skip: !hasOpsGrant });
  const residentEmail = hasOpsGrant
    ? (userData as { user?: { email?: string } } | undefined)?.user?.email
    : user?.email;

  useEffect(() => {
    if (!canViewApplications || !residentEmail) return;
    getApplications({ email: residentEmail }).then((res) => {
      const { data } = res as { data: any[] };
      if (Array.isArray(data)) {
        setApplicationsList(data);
      } else {
        setApplicationsList([]);
      }
    });
  }, [canViewApplications, residentEmail, getApplications]);

  const title = useMemo(
    () =>
      t(
        "account.registerProspect.activeApplicationsTitle",
        "Your Active Applications"
      ),
    [t]
  );
  const description = useMemo(
    () =>
      t(
        "account.registerProspect.activeApplicationsDescription",
        "You have active applications for the following units:"
      ),
    [t]
  );

  if (!canViewApplications) {
    return (
      <Box component={"div"} className={"container_content"} sx={{ mt: 8 }}>
        <Typography variant="h3" marginBottom={2}>
          {t("auth.loginToContinue", "Sign in to view applications")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => loginWithRedirect?.()}
        >
          {t("auth.signIn", "Sign In")}
        </Button>
      </Box>
    );
  }

  return (
    <Wrapper>
      <AppBar style={{ alignItems: "center" }}>
        <Toolbar
          sx={{
            maxWidth: { xs: 732, sm: 600 },
            mx: "auto",
            width: "100%",
            px: 2,
          }}
        >
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            aria-controls={open ? "language-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleMenuClick}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="language-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "menu-button",
            }}
          >
            <MenuItem
              onClick={() => handleLanguageChange("en")}
              selected={i18n.language === "en"}
            >
              English
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange("es")}
              selected={i18n.language === "es"}
            >
              Español
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component={"div"}
        className={"container_content"}
        sx={{
          marginTop: 20,
          [`@media(min-width: ${theme.breakpoints.values.sm}px)`]: {
            minHeight: 200 + commonSpacing.desktopPaddingB * 2,
          },
        }}
      >
        <Typography variant="h3" marginBottom={3}>
          {title}
        </Typography>
        <Typography variant="body1" marginBottom={3} color="text.secondary">
          {description}
        </Typography>

        {applicationsList.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t(
              "applications.none",
              "You have no active applications right now."
            )}
          </Typography>
        )}

        {applicationsList.map((app: any, index: number) => {
          const propName =
            app?.property?.name ||
            app?.propertyName ||
            app?.propertyId ||
            t("applications.property", "Property");
          return (
            <Box
              key={index}
              sx={{
                backgroundColor: "#f9f9f9",
                padding: 3,
                borderRadius: 2,
                marginBottom: 2,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
                >
                  {propName}
                </Typography>
                <Box
                  sx={{
                    backgroundColor:
                      app.status === "pending" ? "#fff3cd" : "#d1ecf1",
                    color: app.status === "pending" ? "#856404" : "#0c5460",
                    padding: "4px 12px",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  {app.status}
                </Box>
              </Box>

              <Box sx={{ marginBottom: 2 }}>
                {app.unit && (
                  <Typography variant="body1" sx={{ marginBottom: 0.5 }}>
                    <strong>Unit:</strong> {app.unit}
                  </Typography>
                )}
                {app.moveInDate && (
                  <Typography variant="body1" sx={{ marginBottom: 0.5 }}>
                    <strong>
                      {t("applications.moveInDate", "Move-in Date")}:
                    </strong>{" "}
                    {moment(app.moveInDate).format("MMM DD, YYYY")}
                  </Typography>
                )}
              </Box>

              {(() => {
                // Status → action logic lives in the shared helper so this page
                // and the homepage Pending Applications card can't drift.
                const action = getApplicationAction(app);
                if (!action) return <></>;
                return (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => navigate(action.buildPath())}
                    sx={{ marginTop: 1 }}
                  >
                    {t(action.labelKey, action.labelFallback)}
                  </Button>
                );
              })()}
            </Box>
          );
        })}

        {/* <Box sx={{ marginTop: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "applications.startNewNote",
              "To start a new application, please visit your property's website."
            )}
          </Typography>
        </Box> */}
      </Box>

      <Box>
        <Footer />
      </Box>
    </Wrapper>
  );
};
